import { BRAND, PRODUCTS } from "../../lib/brand";
import { KraftPouch } from "./KraftPouch";
import { MessageCircle, MapPin, Flame } from "lucide-react";

const HeatMeter = ({ level }) => (
  <div className="flex items-center gap-1.5" aria-label={`Heat level ${level} of 3`}>
    <Flame size={13} className="text-mo-terracotta" />
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-2 h-2 rounded-full"
        style={{
          backgroundColor: i < level ? "#B33922" : "rgba(92,82,72,0.18)",
        }}
      />
    ))}
    <span className="ml-1 text-[10px] uppercase tracking-[0.18em] text-mo-ink-soft">
      {level === 0 ? "No heat" : level === 1 ? "Mild" : level === 2 ? "Medium" : "Fiery"}
    </span>
  </div>
);

export const Products = () => {
  const makeWaLink = (name) =>
    `${BRAND.whatsapp.link}?text=${encodeURIComponent(
      `Hi Maati Organics! I'd like to order ${name} (100 GM). Could you share availability and price?`,
    )}`;

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
            — The Collection
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-mo-ink leading-tight">
            Three tins. <span className="italic text-mo-green">Three stories.</span>
          </h2>
          <p className="mt-5 text-mo-ink-soft max-w-xl">
            Each pouch is packed within days of grinding, so the oils stay
            lively and the aroma stays loud. Meet our trio.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-10 lg:gap-12">
          {PRODUCTS.map((p, idx) => (
            <article
              key={p.slug}
              data-testid={`product-card-${p.slug}`}
              className="group relative"
            >
              <div
                className="relative rounded-2xl p-8 pt-10 pb-9 border border-[#5C5248]/12 kraft-grain transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl overflow-hidden"
                style={{
                  background: idx % 2 === 0
                    ? "linear-gradient(180deg, #EAE3D9 0%, #F9F6F0 100%)"
                    : "linear-gradient(180deg, #F9F6F0 0%, #EAE3D9 100%)",
                }}
              >
                {/* Top meta row */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-6 h-6 rounded-full ring-2 ring-white shadow-inner"
                      style={{
                        backgroundColor: p.swatch,
                        boxShadow: "inset 0 -4px 8px rgba(0,0,0,0.3)",
                      }}
                      aria-label={`${p.name} color`}
                    />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-mo-ink-soft">
                      {p.net}
                    </span>
                  </div>
                  <HeatMeter level={p.heat} />
                </div>

                <KraftPouch product={p} size="md" />

                <div className="mt-8 text-center">
                  <h3 className="font-serif-display text-3xl text-mo-ink">
                    {p.name}
                  </h3>
                  <p className="italic text-sm mt-1" style={{ color: p.accent }}>
                    {p.subtitle}
                  </p>

                  <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-mo-ink-soft">
                    <MapPin size={11} /> {p.origin}
                  </div>

                  <p className="mt-4 text-sm text-mo-ink-soft leading-relaxed">
                    {p.description}
                  </p>

                  <div className="mt-5 pt-5 border-t border-dashed border-[#5C5248]/20 text-left">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-mo-ink-soft mb-2">
                      Aroma notes
                    </div>
                    <div className="font-serif-display italic text-[15px] text-mo-ink">
                      {p.aroma}
                    </div>

                    <div className="text-[10px] uppercase tracking-[0.22em] text-mo-ink-soft mt-4 mb-2">
                      Best with
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {p.pairings.map((pair) => (
                        <span
                          key={pair}
                          className="text-[11px] px-2.5 py-1 rounded-full bg-[#F9F6F0] border border-[#5C5248]/15 text-mo-ink-soft"
                        >
                          {pair}
                        </span>
                      ))}
                    </div>
                  </div>

                  <a
                    href={makeWaLink(p.name)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`product-order-${p.slug}`}
                    className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-mo-ink text-mo-cream text-sm font-medium hover:bg-mo-terracotta transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <MessageCircle size={15} />
                    Order on WhatsApp
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
